import type { DataProvider, TradingPair } from 'hopcharts';
import { DEFAULT_EXCHANGE, EXCHANGES } from './exchanges';

/** One market: exchange id + canonical BASE/QUOTE pair. */
export interface SymbolRef {
  exchangeId: string;
  pair: string;
}

/** Storage key, e.g. "binance:BTC/USDT". */
export function symbolKey(ref: SymbolRef): string {
  return `${ref.exchangeId}:${ref.pair}`;
}

export function fromSymbolKey(key: string): SymbolRef | null {
  const i = key.indexOf(':');
  if (i <= 0) return null;
  const exchangeId = key.slice(0, i);
  const pair = key.slice(i + 1);
  return pair.includes('/') ? { exchangeId, pair } : null;
}

/** Ticker as traders write it: "BTCUSDT". */
export function tickerOf(pair: string): string {
  return pair.replace('/', '');
}

/** TradingView-style id: "BINANCE:BTCUSDT". */
export function tvSymbol(ref: SymbolRef): string {
  return `${ref.exchangeId.toUpperCase()}:${tickerOf(ref.pair)}`;
}

export function baseOf(pair: string): string {
  return pair.split('/')[0] ?? pair;
}

export function quoteOf(pair: string): string {
  return pair.split('/')[1] ?? '';
}

const pairCache = new Map<string, Promise<TradingPair[]>>();

/** Pair list per exchange, loaded once per session (the provider caches too). */
export function loadPairs(provider: DataProvider, exchangeId: string): Promise<TradingPair[]> {
  let pending = pairCache.get(exchangeId);
  if (!pending) {
    pending = provider.getPairs ? provider.getPairs(exchangeId) : Promise.resolve([]);
    pending.catch(() => pairCache.delete(exchangeId));
    pairCache.set(exchangeId, pending);
  }
  return pending;
}

function normalizeTicker(raw: string): string {
  return raw.toUpperCase().replace(/[-_/\s]/g, '');
}

/**
 * Resolve user / URL input into a market: "BINANCE:BTCUSDT", "binance:BTC/USDT",
 * "BTC/USDT", "btcusdt", "BTC-EUR". Without an exchange the default exchange is
 * tried first, then the others. Null when nothing matches.
 */
export async function resolveSymbol(provider: DataProvider, input: string): Promise<SymbolRef | null> {
  const raw = input.trim();
  if (!raw) return null;
  let exchangeHint: string | null = null;
  let ticker = raw;
  const colon = raw.indexOf(':');
  if (colon > 0) {
    exchangeHint = raw.slice(0, colon).toLowerCase();
    ticker = raw.slice(colon + 1);
  }
  const wanted = normalizeTicker(ticker);
  const explicitPair = /[/\-_]/.test(ticker)
    ? ticker.toUpperCase().replace(/[-_]/, '/')
    : null;

  const order = exchangeHint
    ? [exchangeHint]
    : [DEFAULT_EXCHANGE, ...EXCHANGES.map((e) => e.id).filter((id) => id !== DEFAULT_EXCHANGE)];

  for (const exchangeId of order) {
    try {
      const pairs = await loadPairs(provider, exchangeId);
      const match =
        (explicitPair && pairs.find((p) => p.symbol === explicitPair)) ||
        pairs.find((p) => normalizeTicker(p.symbol) === wanted);
      if (match) return { exchangeId, pair: match.symbol };
    } catch {
      // Exchange unreachable from here; try the next one.
    }
  }
  // An explicit BASE/QUOTE on a named exchange is still worth trying even if the pair list failed.
  if (exchangeHint && explicitPair) return { exchangeId: exchangeHint, pair: explicitPair };
  return null;
}
